import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles, MessageCircle, Lock, Unlock, X, Send, BadgeCheck, Heart, MapPin } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { useLang } from "../i18n/useLang";
import { getSetup } from "../i18n/appI18n";

function ChatPanel({ match, onClose }) {
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${match.match_id}/messages`);
      setMessages(data.messages);
      setMe(data.me);
    } catch { /* ignore */ }
  }, [match.match_id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = text.trim();
    setText("");
    try {
      await api.post(`/matches/${match.match_id}/messages`, { text: body });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="chat-panel">
      <div className="w-full sm:max-w-md bg-reveal-burgundy border border-reveal-rose/20 rounded-t-3xl sm:rounded-3xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-reveal-rose/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full overflow-hidden bg-black/40">
              {match.other.photo && (
                <img src={photoUrl(match.other.photo)} alt="" className="w-full h-full object-cover"
                  style={{ filter: `blur(${((100 - match.reveal_percent) / 100) * 8}px)` }} />
              )}
            </div>
            <span className="font-heading text-lg text-reveal-cream">{match.other.display_name}</span>
          </div>
          <button onClick={onClose} data-testid="chat-close-button" className="text-reveal-cream/60 hover:text-reveal-cream">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="chat-messages">
          {messages.length === 0 && <p className="text-center text-reveal-cream/40 text-sm mt-8">Say hello 🤍</p>}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_id === me ? "justify-end" : "justify-start"}`}>
              <span className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${
                m.sender_id === me ? "bg-reveal-rose text-black" : "bg-black/40 text-reveal-cream"
              }`}>{m.text}</span>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="p-3 border-t border-reveal-rose/10 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message..."
            data-testid="chat-input"
            className="flex-1 bg-black/40 border border-reveal-rose/30 rounded-full px-4 py-2.5 text-sm text-reveal-cream outline-none focus:border-reveal-rose" />
          <button type="submit" data-testid="chat-send-button" className="w-11 h-11 rounded-full bg-reveal-rose text-black flex items-center justify-center hover:bg-[#c9987b] transition-colors">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Matches() {
  const [matches, setMatches] = useState(undefined);
  const [myPref, setMyPref] = useState("my_country");
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState(null);
  const lang = useLang();
  const setupS = getSetup(lang);

  const locationLabel = (m) => {
    if (myPref === "worldwide") return m.other.country_name || m.other.country || m.other.city || "";
    if (m.distance_km != null) return setupS.distanceAway.replace("{d}", m.distance_km);
    return m.other.city || m.other.country_name || "";
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/matches");
      setMatches(data.matches);
      if (data.my_location_pref) setMyPref(data.my_location_pref);
    } catch {
      setMatches([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/matches/generate");
      toast.success(data.created ? `${data.created} new match${data.created > 1 ? "es" : ""}!` : "No new matches right now");
      setMatches(data.matches);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not find matches");
    } finally {
      setBusy(false);
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/matches/${id}/${action}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  if (matches === undefined) return <p className="text-reveal-cream/50">Loading...</p>;

  return (
    <div data-testid="matches-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-reveal-cream/55">{matches.length} of 5 matches · one connection at a time with Focus Lock</p>
        <button onClick={generate} disabled={busy} data-testid="find-matches-button"
          className="inline-flex items-center gap-2 bg-reveal-rose text-black font-medium px-5 py-2.5 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-60">
          <Sparkles className="w-4 h-4" /> {busy ? "Finding..." : "Find matches"}
        </button>
      </div>

      {matches.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-reveal-rose/20 rounded-2xl" data-testid="matches-empty">
          <Heart className="w-10 h-10 text-reveal-rose/50 mx-auto mb-3" />
          <p className="text-reveal-cream/60">No matches yet. Tap "Find matches" to meet your most compatible people.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {matches.map((m) => (
            <div key={m.match_id} data-testid={`match-card-${m.match_id}`}
              className={`rounded-2xl border overflow-hidden ${m.paused ? "border-reveal-rose/10 opacity-60" : "border-reveal-rose/20"} bg-reveal-burgundy/60`}>
              <div className="relative h-48 bg-black/40">
                {m.other.photo ? (
                  <img src={photoUrl(m.other.photo)} alt="" className="w-full h-full object-cover"
                    style={{ filter: `blur(${((100 - m.reveal_percent) / 100) * 16}px)` }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-reveal-cream/30"><Heart className="w-10 h-10" /></div>
                )}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-xl text-reveal-cream">{m.other.display_name}</span>
                    {m.other.age && <span className="text-reveal-cream/70 text-sm">{m.other.age}</span>}
                    {m.other.verified && <BadgeCheck className="w-4 h-4 text-green-400" />}
                  </div>
                  {m.other.city && <span className="text-xs text-reveal-cream/60">{m.other.city}</span>}
                </div>
                {m.focused && <span className="absolute top-2 left-2 text-[0.6rem] uppercase bg-reveal-rose text-black px-2 py-0.5 rounded-full">Focused</span>}
                {m.paused && <span className="absolute top-2 left-2 text-[0.6rem] uppercase bg-black/70 text-reveal-cream/70 px-2 py-0.5 rounded-full">Paused</span>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between text-xs text-reveal-cream/55 mb-3">
                  <span>Compatibility {Math.round(m.score * 100)}%</span>
                  <span data-testid={`reveal-${m.match_id}`}>Reveal {m.reveal_percent}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-black/40 mb-4 overflow-hidden">
                  <div className="h-full bg-reveal-rose" style={{ width: `${m.reveal_percent}%` }} />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setChat(m)} data-testid={`open-chat-${m.match_id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-reveal-rose/90 text-black text-sm py-2 rounded-full hover:bg-reveal-rose transition-colors">
                    <MessageCircle className="w-4 h-4" /> Chat
                  </button>
                  {m.focused ? (
                    <button onClick={() => act(m.match_id, "unfocus")} data-testid={`unfocus-${m.match_id}`}
                      className="inline-flex items-center gap-1.5 border border-reveal-rose/40 text-reveal-cream text-sm px-3 py-2 rounded-full hover:bg-reveal-rose/10">
                      <Unlock className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={() => act(m.match_id, "focus")} data-testid={`focus-${m.match_id}`}
                      className="inline-flex items-center gap-1.5 border border-reveal-rose/40 text-reveal-cream text-sm px-3 py-2 rounded-full hover:bg-reveal-rose/10">
                      <Lock className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => act(m.match_id, "end")} data-testid={`end-${m.match_id}`}
                    className="inline-flex items-center gap-1.5 border border-reveal-rose/20 text-reveal-cream/50 text-sm px-3 py-2 rounded-full hover:text-red-400 hover:border-red-400/40">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {chat && <ChatPanel match={chat} onClose={() => { setChat(null); load(); }} />}
    </div>
  );
}
